/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { FaEdit, FaFilePdf, FaTrash } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { useAuthContext } from "../../context/auth";
import { calcularLedgerComisionEdificio } from "../../utils/calcularLedgerComisionEdificio";
import {
  FECHA_CORTE,
  buildSaldoInicialEntry,
  construirLedgerCliente,
  construirLedgerProveedor,
  desvincularPagoCaja,
  desvincularPagoMBF,
  encontrarSaldoInicial,
  toDisplayCurrency as toDisplayCurrencyRaw,
} from "../../utils/estadoCuenta";
import { getPagosEmpleados } from "../../utils/pagosEmpleados";
import { getSaldosIniciales, upsertSaldoInicial } from "../../utils/saldosIniciales";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";
import FacturaExternaModal from "../FacturaExternaModal";
import { Header } from "../Header";
import VincularMovimientoModal from "../VincularMovimientoModal";
import styles from "./EstadoCuenta.module.scss";

// Helpers de periodo_mes — TZ-safe (no usar toISOString en husos negativos).
// Duplicados del modal de carga a propósito; FacturaExternaModal no debe modificarse en esta fase.
const MESES_LABELS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

// Cuentas ER agrupadas por grupo (Costos Fijos / Variables) para el selector de Gasto.
// Mismo patrón que AsignarRecibidaModal.
const CUENTAS_POR_GRUPO = {};
CUENTAS_ER.forEach((c) => {
  if (!CUENTAS_POR_GRUPO[c.grupo]) CUENTAS_POR_GRUPO[c.grupo] = [];
  CUENTAS_POR_GRUPO[c.grupo].push(c);
});

const defaultPeriodoMes = (fechaStr) => {
  if (!fechaStr) {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }
  const [y, m] = fechaStr.split("-").map(Number);
  return new Date(y, m - 1, 1);
};

const periodoMesToString = (date) => {
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
};

const SUB_TABS = [
  { value: "proveedor", label: "Proveedores" },
  { value: "cliente", label: "Clientes" },
  { value: "empleado", label: "Empleados" },
  { value: "edificio", label: "Edificios" },
  { value: "caja_chica", label: "Caja Chica" },
];

const VISTAS_EDIFICIO = [
  { value: "mensual", label: "Mensual" },
  { value: "trimestral", label: "Trimestral" },
  { value: "anual", label: "Anual" },
];

const TIPO_PAGO_LABEL = {
  sueldo: "Sueldo",
  adelanto: "Adelanto",
  comision: "Comisión",
  bono: "Bono",
  finiquito: "Finiquito",
  transporte: "Transporte",
};

const formatFechaDisplay = (fechaStr) => {
  if (!fechaStr) return "-";
  const f = fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
  const [year, month, day] = f.split("-");
  return `${day}/${month}/${year}`;
};

const getTrimestreFromDate = (fechaStr) => {
  if (!fechaStr) return "";
  const month = parseInt(fechaStr.substring(5, 7), 10);
  const year = fechaStr.substring(0, 4);
  const q = Math.ceil(month / 3);
  return `${year} Q${q}`;
};

const agruparPorTrimestre = (entries) => {
  const grouped = {};
  entries.forEach((e) => {
    const key = e.trimestre || getTrimestreFromDate(e.fecha);
    if (!key) return;
    if (!grouped[key]) {
      grouped[key] = {
        fecha: e.fecha,
        trimestre: key,
        concepto: key,
        referencia: "-",
        debito: 0,
        credito: 0,
        moneda: e.moneda,
      };
    }
    grouped[key].debito += e.debito;
    grouped[key].credito += e.credito;
  });
  return Object.values(grouped).sort((a, b) => a.fecha.localeCompare(b.fecha));
};

const agruparPorAno = (entries) => {
  const grouped = {};
  entries.forEach((e) => {
    const year = e.fecha ? e.fecha.substring(0, 4) : "?";
    if (!grouped[year]) {
      grouped[year] = {
        fecha: `${year}-12-31`,
        concepto: year,
        referencia: "-",
        debito: 0,
        credito: 0,
        moneda: e.moneda,
      };
    }
    grouped[year].debito += e.debito;
    grouped[year].credito += e.credito;
  });
  return Object.values(grouped).sort((a, b) => a.fecha.localeCompare(b.fecha));
};

// Display de la columna Concepto, reglas estrictas:
//   1. Factura (origen SII/EXT): "Factura {ref}" o "Factura externa {ref}".
//   2. Descripción del movimiento: se muestra tal cual, sin prefijo.
//   3. Solo referencia.
//   4. Vacío.
// Nunca se prefija con "Pago", "Pago a cuenta", "Cobro" u otros conceptos genéricos.
const conceptoFull = (entry) => {
  const ref = entry.referencia && entry.referencia !== "-" ? entry.referencia : null;
  // NC (bucket "nota_credito") es origen SII pero NO es una factura — distinguir el concepto
  // para que no se confunda con un pago/cobro al verse en el haber (ver regla 10.42).
  if (entry.bucket === "nota_credito") return ref ? `Nota de crédito ${ref}` : "Nota de crédito";
  if (entry.origen === "SII") return ref ? `Factura ${ref}` : "Factura";
  if (entry.origen === "EXT") return entry.concepto || (ref ? `Factura externa ${ref}` : "Factura externa");
  if (entry.descripcion) return entry.descripcion;
  if (entry.concepto) return entry.concepto;
  return ref || "";
};

// Texto a mostrar: truncado a 80 chars (el completo va en el title del <td>).
const renderConcepto = (entry) => {
  const full = conceptoFull(entry);
  return full.length > 80 ? `${full.slice(0, 80)}...` : full;
};

const EstadoCuenta = () => {
  const { pais, id: usuarioId } = useAuthContext();
  const { formatAmount } = useCurrency();

  const normalizedPais = pais ? pais.charAt(0).toUpperCase() + pais.slice(1).toLowerCase() : "";
  const monedaPrincipal = normalizedPais === "Chile" ? "CLP" : "PEN";

  const [subTab, setSubTab] = useState("proveedor");
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const [monedaSaldo, setMonedaSaldo] = useState(monedaPrincipal);
  // Filtro de tipo de documento para las filas del ledger ("todos" | "factura" | "nota_credito" | "pago").
  // Solo afecta el render de la tabla, nunca los boxes. Ver Fase 3.
  const [filtroTipo, setFiltroTipo] = useState("todos");
  // Resetear el filtro al cambiar de entidad o de sub-tab (el filtro no debe persistir entre selecciones).
  useEffect(() => {
    setFiltroTipo("todos");
  }, [subTab, selectedEntityId]);
  const [vistaEdificio, setVistaEdificio] = useState("mensual");
  const [searchParams] = useSearchParams();

  const [proveedores, setProveedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [movimientosCaja, setMovimientosCaja] = useState([]);
  const [siiRecibidas, setSiiRecibidas] = useState([]);
  const [siiEmitidas, setSiiEmitidas] = useState([]);
  const [facturasExternas, setFacturasExternas] = useState([]);
  const [vinculacionesMBF, setVinculacionesMBF] = useState([]);
  const [pagosEmpleados, setPagosEmpleados] = useState([]);
  const [comisionesEdificio, setComisionesEdificio] = useState([]);
  const [cajaChicaGastos, setCajaChicaGastos] = useState([]);
  const [loadingComisiones, setLoadingComisiones] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saldosIniciales, setSaldosIniciales] = useState([]);
  const [editandoSaldo, setEditandoSaldo] = useState(false);
  const [mostrarFacturaModal, setMostrarFacturaModal] = useState(false);
  const [mostrarVincularCajaChica, setMostrarVincularCajaChica] = useState(false);
  const [editandoFacturaExt, setEditandoFacturaExt] = useState(null);
  const [showVincularModal, setShowVincularModal] = useState(false);
  const [movimientoParaVincular, setMovimientoParaVincular] = useState(null);

  // ─── Carga inicial de datos ───
  useEffect(() => {
    if (!normalizedPais) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      client
        .from("proveedores")
        .select("id, nombre, moneda_recurrente, es_recurrente")
        .eq("pais", normalizedPais)
        .order("nombre"),
      client.from("clientes").select("id, nombre, empresa").eq("pais", normalizedPais),
      client
        .from("empleados")
        .select("id, usuario_id, nombre, apellido, pais, egreso_fecha")
        .eq("pais", normalizedPais)
        .order("nombre"),
      client
        .from("edificios")
        // Campos de comisión necesarios para calcularLedgerComisionEdificio (mismo set que
        // getEdificiosSaldoPendiente). Sin ellos el edificiosMap llega incompleto → comisión 0
        // → ledger vacío ("Sin movimientos"). pantallas/espera/totems/cowork + tramos % + fija.
        .select(
          "id, edificio, alianza, pais, pantallas, totems, espera, cowork, " +
            "comision_1_fechainicio, comision_1_fechafin, comision_1_porcentaje, " +
            "comision_2_fechainicio, comision_2_fechafin, comision_2_porcentaje, " +
            "comision_3_fechainicio, comision_3_fechafin, comision_3_porcentaje, " +
            "comision_fija_monto, comision_fija_inicio, comision_fija_fin, comision_fija_moneda"
        )
        .eq("pais", normalizedPais),
      client
        .from("movimientos_caja")
        .select(
          "id, fecha, monto, moneda, tipo, entidad_tipo, entidad_id, descripcion, numero_documento, categoria_pago, estado, movimiento_bancario_id"
        )
        .eq("pais", normalizedPais)
        .neq("estado", "anulado")
        .gte("fecha", FECHA_CORTE)
        .order("fecha", { ascending: false }),
      client
        .from("facturas_sii_recibidas")
        .select(
          "id, proveedor_id, fecha_emision, monto_total, folio, tipo_dte, razon_social_emisor, estado_vinculacion"
        )
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      client
        .from("facturas_sii_emitidas")
        .select("id, cliente_id, fecha_emision, monto_total, folio, tipo_dte")
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      client
        .from("facturas_externas")
        .select(
          "id, proveedor_id, fecha_emision, monto_total, moneda, numero_documento, estado, concepto, archivo_url, periodo_mes, movimiento_bancario_id"
        )
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      getPagosEmpleados({ pais: normalizedPais, desde: FECHA_CORTE }),
      getSaldosIniciales(normalizedPais),
      client
        .from("caja_chica_gastos")
        .select("id, empleado_id, factura_tipo, factura_id, monto, fecha, concepto, pais")
        .eq("pais", normalizedPais),
      // Vinculaciones directas pago↔factura (MBF). El ledger las usa para saldar al 100% las
      // facturas vinculadas e igualar el crédito del pago al débito de la factura (evita el
      // residuo cambiario fantasma cuando un pago local salda 1:1 una factura en otra moneda).
      client.from("movimiento_bancario_facturas").select("movimiento_bancario_id, factura_tipo, factura_id, monto"),
    ])
      .then(([provs, clis, emps, edifs, movs, sR, sE, fE, pE, sI, ccG, mbf]) => {
        if (cancelled) return;
        setProveedores(provs.data || []);
        setClientes(clis.data || []);
        setEmpleados(emps.data || []);
        setEdificios(edifs.data || []);
        setMovimientosCaja(movs.data || []);
        setSiiRecibidas(sR.data || []);
        setSiiEmitidas(sE.data || []);
        setFacturasExternas(fE.data || []);
        setPagosEmpleados(pE || []);
        setSaldosIniciales(sI || []);
        setCajaChicaGastos(ccG.data || []);
        setVinculacionesMBF(mbf.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error cargando estado de cuenta:", err);
        toast.error("Error cargando estado de cuenta");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [normalizedPais]);

  // ─── Preselección via URL params (?tipo=proveedor&id=X) ───
  useEffect(() => {
    const tipo = searchParams.get("tipo");
    const id = searchParams.get("id");
    if (!tipo || !id) return;
    if (tipo === "proveedor" && proveedores.length > 0) {
      setSubTab("proveedor");
      setSelectedEntityId(Number(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveedores]);

  // ─── Auto-detectar moneda predominante al elegir proveedor ───
  // Si hay facturas cargadas → usar la moneda mayoritaria. Si no hay facturas todavía
  // → fallback a proveedor.moneda_recurrente (configurada en GestionProveedores). Último
  // recurso: monedaPrincipal del país.
  useEffect(() => {
    if (subTab !== "proveedor" || !selectedEntityId) return;
    const provId = Number(selectedEntityId);
    const fxs = facturasExternas.filter((f) => f.proveedor_id === provId && f.estado !== "anulada");
    const total = fxs.length;
    if (total === 0) {
      const proveedor = proveedores.find((p) => p.id === provId);
      setMonedaSaldo(proveedor?.moneda_recurrente || monedaPrincipal);
      return;
    }
    const usdCount = fxs.filter((f) => f.moneda === "USD").length;
    setMonedaSaldo(usdCount > total / 2 ? "USD" : monedaPrincipal);
  }, [selectedEntityId, subTab, facturasExternas, monedaPrincipal, proveedores]);

  // ─── Resolver IDs de edificios según selección ───
  const resolverEdificioIds = useCallback(
    (entityId) => {
      if (!entityId) return [];
      if (entityId === "todos") return edificios.map((e) => e.id);
      if (entityId.startsWith("alianza:")) {
        const alianza = entityId.replace("alianza:", "");
        return edificios.filter((e) => e.alianza === alianza).map((e) => e.id);
      }
      return [Number(entityId)];
    },
    [edificios]
  );

  // ─── Lazy-load comisiones edificio ───
  useEffect(() => {
    if (subTab !== "edificio" || !selectedEntityId) {
      setComisionesEdificio([]);
      return;
    }
    let cancelled = false;
    const fetchComisiones = async () => {
      setLoadingComisiones(true);
      try {
        const ids = resolverEdificioIds(selectedEntityId);
        if (ids.length === 0) {
          if (!cancelled) setComisionesEdificio([]);
          return;
        }
        const edificiosMap = {};
        edificios.forEach((e) => {
          edificiosMap[e.id] = e;
        });
        const result = await calcularLedgerComisionEdificio({
          edificioIds: ids,
          edificiosMap,
          fechaInicio: FECHA_CORTE,
          fechaFin: `${new Date().getFullYear() + 1}-12-31`,
          pais: normalizedPais,
        });
        if (!cancelled) setComisionesEdificio(result);
      } catch (err) {
        console.error("Error calculando comisiones edificio:", err);
        if (!cancelled) setComisionesEdificio([]);
      } finally {
        if (!cancelled) setLoadingComisiones(false);
      }
    };
    fetchComisiones();
    return () => {
      cancelled = true;
    };
  }, [subTab, selectedEntityId, edificios, normalizedPais, resolverEdificioIds]);

  // ─── Helper de conversión moneda ───
  // Wrapper que cierra sobre {monedaSaldo, monedaPrincipal, pais} para que las
  // llamadas en los ledgers se mantengan en forma de 3 args (monto, moneda, fecha).
  const toDisplayCurrency = useCallback(
    (monto, monedaOrigen, fecha) =>
      toDisplayCurrencyRaw(monto, monedaOrigen, fecha, { monedaSaldo, monedaPrincipal, pais: normalizedPais }),
    [monedaSaldo, monedaPrincipal, normalizedPais]
  );

  // ─── Opciones del dropdown ───
  const uniqueAlianzas = useMemo(
    () => [...new Set(edificios.map((e) => e.alianza).filter(Boolean))].sort(),
    [edificios]
  );

  const entityOptions = useMemo(() => {
    if (subTab === "proveedor") return proveedores.map((p) => ({ value: p.id, label: p.nombre }));
    if (subTab === "cliente") {
      return [...clientes]
        .sort((a, b) => (a.empresa || a.nombre || "").localeCompare(b.empresa || b.nombre || ""))
        .map((c) => ({ value: c.id, label: c.empresa || c.nombre }));
    }
    if (subTab === "empleado") {
      return empleados.filter((e) => !e.egreso_fecha).map((e) => ({ value: e.id, label: `${e.nombre} ${e.apellido}` }));
    }
    if (subTab === "caja_chica") {
      return empleados.filter((e) => !e.egreso_fecha).map((e) => ({ value: e.id, label: `${e.nombre} ${e.apellido}` }));
    }
    return [];
  }, [subTab, proveedores, clientes, empleados]);

  const entityLabel =
    subTab === "proveedor"
      ? "Proveedor"
      : subTab === "cliente"
        ? "Cliente"
        : subTab === "empleado"
          ? "Empleado"
          : subTab === "caja_chica"
            ? "Empleado"
            : "Edificio";

  // ─── Saldo inicial de la entidad activa (si aplica) ───
  // Solo se busca para entidades simples (no para "todos" ni alianzas en edificios).
  const saldoInicialActivo = useMemo(
    () => encontrarSaldoInicial(saldosIniciales, subTab, selectedEntityId),
    [saldosIniciales, subTab, selectedEntityId]
  );

  // ─── LEDGER: PROVEEDORES (3 fuentes + pagos) ───
  const ledgerProveedor = useMemo(() => {
    if (subTab !== "proveedor" || !selectedEntityId) return [];
    return construirLedgerProveedor({
      provId: Number(selectedEntityId),
      siiRecibidas,
      facturasExternas,
      movimientosCaja,
      saldoInicial: saldoInicialActivo,
      monedaPrincipal,
      toDisplayCurrency,
      vinculacionesDirectas: vinculacionesMBF,
    }).entries;
  }, [
    subTab,
    selectedEntityId,
    siiRecibidas,
    facturasExternas,
    movimientosCaja,
    monedaPrincipal,
    toDisplayCurrency,
    saldoInicialActivo,
    vinculacionesMBF,
  ]);

  // ─── LEDGER: CLIENTES (SII emitidas + cobros) ───
  const ledgerCliente = useMemo(() => {
    if (subTab !== "cliente" || !selectedEntityId) return [];
    return construirLedgerCliente({
      cliId: Number(selectedEntityId),
      siiEmitidas,
      movimientosCaja,
      saldoInicial: saldoInicialActivo,
      monedaPrincipal,
      toDisplayCurrency,
    });
  }, [subTab, selectedEntityId, siiEmitidas, movimientosCaja, monedaPrincipal, toDisplayCurrency, saldoInicialActivo]);

  // ─── LEDGER: EMPLEADOS (pagos_empleados) ───
  const ledgerEmpleado = useMemo(() => {
    if (subTab !== "empleado" || !selectedEntityId) return [];
    const empId = Number(selectedEntityId);

    const pagos = pagosEmpleados
      .filter((p) => p.empleado_id === empId && (p.fecha_pago || "") >= FECHA_CORTE)
      .map((p) => ({
        fecha: p.fecha_pago,
        concepto: TIPO_PAGO_LABEL[p.tipo] || p.tipo || "Pago",
        referencia: p.periodo_año && p.periodo_mes ? `${String(p.periodo_mes).padStart(2, "0")}/${p.periodo_año}` : "-",
        debito: 0,
        credito: Number(p.monto || 0),
        moneda: p.moneda || monedaPrincipal,
      }));

    const inicial = saldoInicialActivo ? [buildSaldoInicialEntry(saldoInicialActivo, monedaPrincipal)] : [];
    const entries = [...inicial, ...pagos].sort((a, b) => a.fecha.localeCompare(b.fecha));

    let saldo = 0;
    return entries
      .map((entry) => {
        const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
        const creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
        saldo += debitoDisplay - creditoDisplay;
        return { ...entry, debitoDisplay, creditoDisplay, saldo };
      })
      .reverse();
  }, [subTab, selectedEntityId, pagosEmpleados, monedaPrincipal, toDisplayCurrency, saldoInicialActivo]);

  // ─── LEDGER: EDIFICIOS (comisiones + pagos) ───
  const ledgerEdificio = useMemo(() => {
    if (subTab !== "edificio" || !selectedEntityId) return [];

    const targetIds = new Set(resolverEdificioIds(selectedEntityId));

    const debitos = comisionesEdificio.map((c) => ({
      fecha: c.fecha,
      trimestre: c.trimestre,
      concepto: c.concepto,
      referencia: "-",
      debito: c.debito,
      credito: 0,
      moneda: c.moneda,
    }));

    const creditos = movimientosCaja
      .filter((m) => m.tipo === "pago" && m.entidad_tipo === "edificio" && targetIds.has(m.entidad_id))
      .map((m) => ({
        fecha: m.fecha,
        trimestre: getTrimestreFromDate(m.fecha),
        concepto: "Pago",
        referencia: m.numero_documento || "-",
        debito: 0,
        credito: Number(m.monto),
        moneda: m.moneda || monedaPrincipal,
      }));

    // Saldo inicial solo aplica cuando es un edificio individual (no "todos" ni alianza)
    const inicialEntry = saldoInicialActivo
      ? [
          {
            ...buildSaldoInicialEntry(saldoInicialActivo, monedaPrincipal),
            trimestre: getTrimestreFromDate(FECHA_CORTE),
          },
        ]
      : [];
    let entries = [...inicialEntry, ...debitos, ...creditos].sort((a, b) => a.fecha.localeCompare(b.fecha));

    if (vistaEdificio === "trimestral") entries = agruparPorTrimestre(entries);
    else if (vistaEdificio === "anual") entries = agruparPorAno(entries);

    let saldo = 0;
    return entries
      .map((entry) => {
        const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
        const creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
        saldo += debitoDisplay - creditoDisplay;
        return { ...entry, debitoDisplay, creditoDisplay, saldo };
      })
      .reverse();
  }, [
    subTab,
    selectedEntityId,
    comisionesEdificio,
    movimientosCaja,
    vistaEdificio,
    monedaPrincipal,
    toDisplayCurrency,
    resolverEdificioIds,
    saldoInicialActivo,
  ]);

  // ─── LEDGER: CAJA CHICA (entregas de fondos + gastos con factura) ───
  // DEBE = entregas de fondos (movimientos_caja categoria_pago='caja_chica').
  // HABER = gastos con factura (caja_chica_gastos). Saldo > 0 = fondos sin rendir.
  const ledgerCajaChica = useMemo(() => {
    if (subTab !== "caja_chica" || !selectedEntityId) return [];

    // Resolver usuario_id (INT) del empleado seleccionado (UUID) para filtrar movimientos_caja
    const emp = empleados.find((e) => String(e.id) === String(selectedEntityId));
    const usuarioId = emp?.usuario_id;

    // DEBE: entregas de fondos — movimientos_caja con categoria_pago='caja_chica' para este empleado
    const entregas = movimientosCaja
      .filter((m) => m.categoria_pago === "caja_chica" && m.entidad_tipo === "empleado" && m.entidad_id === usuarioId)
      .map((m) => ({
        fecha: m.fecha,
        concepto: m.descripcion || "Entrega de fondos",
        referencia: m.numero_documento || "-",
        debito: Number(m.monto || 0),
        credito: 0,
        moneda: m.moneda || monedaPrincipal,
      }));

    // HABER: gastos con factura — caja_chica_gastos de este empleado
    const gastos = cajaChicaGastos
      .filter((g) => g.empleado_id === selectedEntityId)
      .map((g) => ({
        fecha: g.fecha,
        concepto: g.concepto || "Gasto con factura",
        referencia: "-",
        debito: 0,
        credito: Number(g.monto || 0),
        moneda: monedaPrincipal,
        _cajaChicaGastoId: g.id,
        _facturaId: g.factura_id,
        _facturaTipo: g.factura_tipo,
      }));

    const saldoIni = saldoInicialActivo ? [buildSaldoInicialEntry(saldoInicialActivo, monedaPrincipal)] : [];
    const entries = [...saldoIni, ...entregas, ...gastos].sort((a, b) => a.fecha.localeCompare(b.fecha));

    let saldo = 0;
    return entries
      .map((entry) => {
        const debitoDisplay = toDisplayCurrency(entry.debito, entry.moneda, entry.fecha);
        const creditoDisplay = toDisplayCurrency(entry.credito, entry.moneda, entry.fecha);
        saldo += debitoDisplay - creditoDisplay;
        return { ...entry, debitoDisplay, creditoDisplay, saldo };
      })
      .reverse();
  }, [
    subTab,
    selectedEntityId,
    empleados,
    movimientosCaja,
    cajaChicaGastos,
    monedaPrincipal,
    toDisplayCurrency,
    saldoInicialActivo,
  ]);

  const ledger =
    subTab === "proveedor"
      ? ledgerProveedor
      : subTab === "cliente"
        ? ledgerCliente
        : subTab === "empleado"
          ? ledgerEmpleado
          : subTab === "caja_chica"
            ? ledgerCajaChica
            : ledgerEdificio;

  // Boxes universales (todos los sub-tabs). Las NCs (bucket "nota_credito") se separan en su
  // propio box: facturado bruto (solo DEBE de facturas) − notas de crédito (HABER de NCs) =
  // facturado neto. Cuando no hay NCs, totalNotasCredito=0 y el layout colapsa a 4 boxes.
  const totalFacturadoBruto = ledger
    .filter((e) => !e.bucket || e.bucket === "factura")
    .reduce((s, e) => s + e.debitoDisplay, 0);
  const totalNotasCredito = ledger.filter((e) => e.bucket === "nota_credito").reduce((s, e) => s + e.creditoDisplay, 0);
  const totalFacturadoNeto = totalFacturadoBruto - totalNotasCredito;
  const totalPagado = ledger
    .filter((e) => !e.bucket || e.bucket === "factura")
    .reduce((s, e) => s + e.creditoDisplay, 0);
  const totalDebeCredito = ledger.filter((e) => e.bucket === "credito").reduce((s, e) => s + e.debitoDisplay, 0);
  const totalHaberCredito = ledger.filter((e) => e.bucket === "credito").reduce((s, e) => s + e.creditoDisplay, 0);
  const creditoNeto = totalDebeCredito - totalHaberCredito;
  // Debe cuadrar con el saldo corrido de la última entry del ledger (identidad matemática 4.9).
  const saldoFinal = totalFacturadoNeto - totalPagado + creditoNeto;

  // ─── Filtro por tipo de documento (solo afecta las filas renderizadas, NO los boxes) ───
  const hayNCenLedger = ledger.some((e) => e.bucket === "nota_credito");
  // Predicado: factura = lado DEBE de facturas (incl. entries sin bucket: empleado/edificio);
  // pago = lado HABER que no sea NC; nota_credito = bucket dedicado.
  const pasaFiltroTipo = (e) => {
    if (filtroTipo === "nota_credito") return e.bucket === "nota_credito";
    if (filtroTipo === "factura") return (!e.bucket || e.bucket === "factura") && e.debitoDisplay > 0;
    if (filtroTipo === "pago") {
      return (!e.bucket || e.bucket === "factura") && e.creditoDisplay > 0 && e.bucket !== "nota_credito";
    }
    if (filtroTipo === "credito") return e.bucket === "credito";
    return true;
  };
  const ledgerFiltrado = filtroTipo === "todos" ? ledger : ledger.filter(pasaFiltroTipo);

  // El botón "Editar saldo inicial" solo tiene sentido para entidades simples (no agregaciones).
  const puedeEditarSaldoInicial = !!selectedEntityId && Number.isFinite(Number(selectedEntityId));

  // Mapa movimiento_bancario_id → folio/nº de la factura vinculada vía MBF. Lo usa la columna
  // TIPO del ledger para mostrar "Factura #{folio}" en pagos con vinculación directa. Toma la
  // primera factura vinculada del movimiento (un pago puede saldar varias). Lookup por id
  // (regla 10.7): sii_recibida→siiRecibidas.folio, factura_externa→facturasExternas.numero_documento.
  const folioPorMovBancario = useMemo(() => {
    const map = new Map();
    for (const v of vinculacionesMBF) {
      if (v.movimiento_bancario_id == null || map.has(v.movimiento_bancario_id)) continue;
      let folio = null;
      if (v.factura_tipo === "sii_recibida") {
        folio = siiRecibidas.find((f) => f.id === v.factura_id)?.folio ?? null;
      } else if (v.factura_tipo === "factura_externa") {
        folio = facturasExternas.find((f) => f.id === v.factura_id)?.numero_documento ?? null;
      }
      if (folio != null) map.set(v.movimiento_bancario_id, String(folio));
    }
    return map;
  }, [vinculacionesMBF, siiRecibidas, facturasExternas]);

  const handleGuardarSaldoInicial = async ({ monto, moneda, notas }) => {
    if (!puedeEditarSaldoInicial) return;
    try {
      const saved = await upsertSaldoInicial({
        pais: normalizedPais,
        entidad_tipo: subTab,
        entidad_id: Number(selectedEntityId),
        monto,
        moneda,
        notas,
      });
      setSaldosIniciales((prev) => {
        const idx = prev.findIndex((s) => s.id === saved.id);
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = saved;
          return copy;
        }
        return [...prev, saved];
      });
      toast.success("Saldo inicial guardado");
      setEditandoSaldo(false);
    } catch (err) {
      console.error("Error guardando saldo inicial:", err);
      toast.error("Error guardando saldo inicial");
    }
  };

  // Desvincula un gasto de caja chica: borra la fila de caja_chica_gastos y devuelve la
  // factura SII a "pendiente". Solo aplica a entries de HABER (con _cajaChicaGastoId).
  // Las entregas de fondos (DEBE) se desvinculan desde FlujoCajaV2 (handleDesvincularCaja).
  const handleDesvincularCajaChica = async (entry) => {
    if (!window.confirm("¿Desvincular esta factura de caja chica?")) return;
    try {
      const { error: delError } = await client.from("caja_chica_gastos").delete().eq("id", entry._cajaChicaGastoId);
      if (delError) throw delError;

      if (entry._facturaTipo === "sii_recibida" && entry._facturaId) {
        await client
          .from("facturas_sii_recibidas")
          .update({ estado_vinculacion: "pendiente", tipo_contable: null, cuenta_er: null, vinculado_at: null })
          .eq("id", entry._facturaId);
      }

      setCajaChicaGastos((prev) => prev.filter((g) => g.id !== entry._cajaChicaGastoId));
      toast.success("Factura desvinculada de caja chica");
    } catch (err) {
      console.error("Error desvinculando:", err);
      toast.error("Error al desvincular: " + (err.message || ""));
    }
  };

  const handleDeleteFacturaExt = async (entry) => {
    const labelEntidad = entry.concepto || entry.referencia || "esta factura";
    if (!window.confirm(`¿Eliminar "${labelEntidad}"? Esta acción no se puede deshacer.`)) return;
    try {
      // Borrar filas puente en movimiento_bancario_facturas (alimentan los badges V/C/X en
      // FlujoCajaV2). Sin esto quedan huérfanas apuntando a una factura ya borrada y el badge
      // persiste. Fallo se loguea pero no aborta (patrón del resto del handler).
      const { error: mbfErr } = await client
        .from("movimiento_bancario_facturas")
        .delete()
        .eq("factura_tipo", "factura_externa")
        .eq("factura_id", entry.facturaId);
      if (mbfErr) console.error("Error eliminando filas en movimiento_bancario_facturas:", mbfErr);

      // Limpiar refs en movimientos_bancarios antes del DELETE para evitar match_factura_id
      // huérfanas (FK polimórfica sin ON DELETE CASCADE). Fallo acá se loguea pero no aborta:
      // preferimos completar la eliminación aunque queden refs huérfanas que limpiar después.
      const { error: refsErr } = await client
        .from("movimientos_bancarios")
        .update({ match_factura_tipo: null, match_factura_id: null })
        .eq("match_factura_tipo", "factura_externa")
        .eq("match_factura_id", entry.facturaId);
      if (refsErr) console.error("Error limpiando refs en movimientos_bancarios:", refsErr);

      // Si la factura vino del flujo bancario (tiene movimiento_bancario_id), hay que
      // deshacer el asiento de caja y devolver el banco a pendiente. Si fue carga manual
      // sin movimiento_bancario_id, no hay nada bancario que limpiar.
      const facturaDb = facturasExternas.find((f) => f.id === entry.facturaId);
      const movBancarioId = facturaDb?.movimiento_bancario_id;
      const proveedorId = facturaDb?.proveedor_id;
      if (movBancarioId) {
        // 3a. Eliminar movimientos_caja generados por esta factura (mismo banco + proveedor).
        if (proveedorId) {
          const { data: cajasEliminadas, error: cajaErr } = await client
            .from("movimientos_caja")
            .delete()
            .eq("movimiento_bancario_id", movBancarioId)
            .eq("entidad_tipo", "proveedor")
            .eq("entidad_id", proveedorId)
            .select("id");
          if (cajaErr) {
            console.error("Error eliminando movimientos_caja vinculados:", cajaErr);
          } else if (cajasEliminadas?.length) {
            const eliminadosIds = new Set(cajasEliminadas.map((m) => m.id));
            setMovimientosCaja((prev) => prev.filter((m) => !eliminadosIds.has(m.id)));
          }
        }

        // 3b. SIEMPRE devolver el banco a pendiente y nulear la FK de caja (haya o no caja
        // que borrar). Antes esto quedaba anidado en el if de caja, así que el banco nunca
        // se reseteaba si no había caja → el movimiento seguía "procesado".
        const { error: bancoErr } = await client
          .from("movimientos_bancarios")
          .update({ movimiento_caja_id: null, estado: "pendiente", procesado_at: null, procesado_por: null })
          .eq("id", movBancarioId);
        if (bancoErr) console.error("Error reseteando movimiento bancario a pendiente:", bancoErr);
      }

      const { error } = await client.from("facturas_externas").delete().eq("id", entry.facturaId);
      if (error) throw error;
      setFacturasExternas((prev) => prev.filter((f) => f.id !== entry.facturaId));
      toast.success("Factura eliminada");
    } catch (err) {
      console.error("Error eliminando factura externa:", err);
      toast.error("Error eliminando factura: " + (err.message || ""));
    }
  };

  const handleFacturaExtEditada = (updated) => {
    setFacturasExternas((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
    setEditandoFacturaExt(null);
  };

  // Desvincular un pago desde una fila de crédito del ledger. Usa el util compartido: si el pago
  // tiene MBF (tieneMBF) libera además las facturas externas vinculadas; si no, solo limpia caja
  // + banco. Refresh optimista de los 3 states para que el ledger (useMemo) recalcule sin reload.
  const handleDesvincularPago = async (entry) => {
    if (!entry.cajaId && !entry.movimientoBancarioId) return;
    if (!window.confirm("¿Desvincular este pago? El movimiento bancario volverá a pendiente.")) return;

    const resultado = entry.tieneMBF
      ? await desvincularPagoMBF({ movimientoBancarioId: entry.movimientoBancarioId, cajaId: entry.cajaId })
      : await desvincularPagoCaja({ movimientoBancarioId: entry.movimientoBancarioId, cajaId: entry.cajaId });

    if (resultado.ok) {
      if (entry.cajaId) {
        setMovimientosCaja((prev) => prev.filter((mc) => mc.id !== entry.cajaId));
      }
      if (resultado.externasLiberadas?.length) {
        setFacturasExternas((prev) =>
          prev.map((fe) =>
            resultado.externasLiberadas.includes(fe.id)
              ? { ...fe, estado: "pendiente", movimiento_bancario_id: null }
              : fe
          )
        );
        setVinculacionesMBF((prev) => prev.filter((v) => v.movimiento_bancario_id !== entry.movimientoBancarioId));
      }
      toast.success("Pago desvinculado");
    } else {
      toast.error("Error al desvincular: " + (resultado.error?.message || "desconocido"));
    }
  };

  // Re-fetch acotado de los states que alimentan el ledger del proveedor (no toca la carga
  // inicial). Se usa tras una vinculación desde el modal, que puede haber tocado caja, MBF,
  // facturas externas y/o el estado_vinculacion de SII recibidas.
  const recargarLedgerData = useCallback(async () => {
    if (!normalizedPais) return;
    const [movs, sR, fE, mbf] = await Promise.all([
      client
        .from("movimientos_caja")
        .select(
          "id, fecha, monto, moneda, tipo, entidad_tipo, entidad_id, descripcion, numero_documento, categoria_pago, estado, movimiento_bancario_id"
        )
        .eq("pais", normalizedPais)
        .neq("estado", "anulado")
        .gte("fecha", FECHA_CORTE)
        .order("fecha", { ascending: false }),
      client
        .from("facturas_sii_recibidas")
        .select(
          "id, proveedor_id, fecha_emision, monto_total, folio, tipo_dte, razon_social_emisor, estado_vinculacion"
        )
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      client
        .from("facturas_externas")
        .select(
          "id, proveedor_id, fecha_emision, monto_total, moneda, numero_documento, estado, concepto, archivo_url, periodo_mes, movimiento_bancario_id"
        )
        .eq("pais", normalizedPais)
        .gte("fecha_emision", FECHA_CORTE),
      client.from("movimiento_bancario_facturas").select("movimiento_bancario_id, factura_tipo, factura_id, monto"),
    ]);
    setMovimientosCaja(movs.data || []);
    setSiiRecibidas(sR.data || []);
    setFacturasExternas(fE.data || []);
    setVinculacionesMBF(mbf.data || []);
  }, [normalizedPais]);

  // Abre VincularMovimientoModal para un pago sin vincular. Carga el movimiento bancario con los
  // mismos embeds que getMovimientosBancarios (el modal espera factura_*, movimiento_caja, etc.).
  const handleAbrirVincular = async (entry) => {
    if (!entry.movimientoBancarioId) return;
    const { data: mb, error } = await client
      .from("movimientos_bancarios")
      .select(
        `*,
        factura_emitida:facturas_sii_emitidas(folio, razon_social_receptor, tipo_dte),
        factura_recibida:facturas_sii_recibidas(folio, razon_social_emisor),
        facturas_vinculadas:movimiento_bancario_facturas(factura_tipo, factura_id),
        factura_externa:facturas_externas(numero_documento, archivo_url),
        movimiento_caja:movimientos_caja!movimiento_bancario_id(categoria_pago, entidad_nombre)`
      )
      .eq("id", entry.movimientoBancarioId)
      .single();

    if (error || !mb) {
      toast.error("No se pudo cargar el movimiento bancario");
      return;
    }

    setMovimientoParaVincular(mb);
    setShowVincularModal(true);
  };

  // Tras confirmar una vinculación, recarga los datos del ledger (más seguro que optimista
  // parcial: el modal pudo tocar MBF, caja, externas y SII).
  const handleVincularSuccess = () => {
    setShowVincularModal(false);
    setMovimientoParaVincular(null);
    recargarLedgerData();
  };

  // Desasocia una factura SII recibida mal matcheada del proveedor (basura del auto-match SII).
  // Vuelve a 'pendiente' y limpia metadata de vinculación — mismo shape que reactivarFactura
  // en conciliacionSII.js. (Las columnas reales son estado_vinculacion / vinculado_*, no
  // estado_matching / confianza_match.)
  const handleDesasociarSII = async (entry) => {
    if (!entry.facturaId) return;
    if (!window.confirm("¿Desasociar esta factura SII del proveedor? Volverá a estado pendiente de matching.")) return;
    try {
      const { error } = await client
        .from("facturas_sii_recibidas")
        .update({
          proveedor_id: null,
          estado_vinculacion: "pendiente",
          vinculado_por: null,
          vinculado_at: null,
        })
        .eq("id", entry.facturaId);
      if (error) throw error;
      setSiiRecibidas((prev) => prev.filter((f) => f.id !== entry.facturaId));
      toast.success("Factura SII desasociada del proveedor");
    } catch (err) {
      console.error("Error desasociando factura SII:", err);
      toast.error("Error al desasociar: " + (err.message || ""));
    }
  };

  // Reclasifica un pago cambiando SOLO categoria_pago (bucket Factura/A cuenta/Crédito).
  // No toca vinculaciones MBF ni el movimiento bancario — solo el grano contable del pago.
  // Refresh optimista de movimientosCaja → el ledger (useMemo) recalcula bucket/boxes/TIPO.
  const handleCambiarTipoPago = async (entry, nuevaCategoria) => {
    if (!entry.cajaId || !nuevaCategoria || nuevaCategoria === entry.categoriaPago) return;
    try {
      const { error } = await client
        .from("movimientos_caja")
        .update({ categoria_pago: nuevaCategoria })
        .eq("id", entry.cajaId);
      if (error) throw error;
      setMovimientosCaja((prev) =>
        prev.map((m) => (m.id === entry.cajaId ? { ...m, categoria_pago: nuevaCategoria } : m))
      );
      toast.success("Tipo de pago actualizado");
    } catch (err) {
      console.error("Error cambiando tipo de pago:", err);
      toast.error("Error al cambiar tipo: " + (err.message || ""));
    }
  };

  // Edita el concepto (descripcion) de un movimiento de caja (pago/cobro). Solo cambia
  // movimientos_caja.descripcion. Refresh optimista → el ledger recalcula sin reload.
  const handleCambiarDescripcion = async (entry, nuevaDesc) => {
    if (!entry.cajaId) return;
    const desc = (nuevaDesc ?? "").trim();
    if (desc === (entry.descripcion || "")) return; // sin cambios → no hace nada
    try {
      const { error } = await client.from("movimientos_caja").update({ descripcion: desc }).eq("id", entry.cajaId);
      if (error) throw error;
      setMovimientosCaja((prev) => prev.map((m) => (m.id === entry.cajaId ? { ...m, descripcion: desc } : m)));
      toast.success("Concepto actualizado");
    } catch (err) {
      console.error("Error cambiando concepto:", err);
      toast.error("Error al cambiar concepto: " + (err.message || ""));
    }
  };

  // ─── RENDER ───
  return (
    <div className={styles.wrapper}>
      <Header
        leftContent={
          <div style={{ display: "flex", alignItems: "center", marginLeft: 24 }}>
            <div
              style={{
                display: "flex",
                gap: 0,
                borderRadius: 6,
                overflow: "hidden",
                border: "1px solid #dee2e6",
              }}
            >
              {SUB_TABS.map((st) => (
                <button
                  key={st.value}
                  type="button"
                  onClick={() => {
                    setSubTab(st.value);
                    setSelectedEntityId("");
                    setMonedaSaldo(monedaPrincipal);
                  }}
                  style={{
                    height: 32,
                    padding: "0 18px",
                    fontSize: "0.82rem",
                    fontWeight: 400,
                    border: "none",
                    cursor: "pointer",
                    background: subTab === st.value ? "#2c3e50" : "transparent",
                    color: subTab === st.value ? "#fff" : "#666",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {st.label}
                </button>
              ))}
            </div>
          </div>
        }
      >
        {/* Botones a la izquierda del selector — el selector va último para que quede anclado
            a la derecha del rightGroup (que es flex con margin-right:2rem). */}
        {!loading && selectedEntityId && ledger.length > 0 && (
          <select
            className={`form-select ${styles.filtroTipoSelect}`}
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
          >
            <option value="todos">Todos</option>
            <option value="factura">Facturas</option>
            {hayNCenLedger && <option value="nota_credito">Notas de crédito</option>}
            <option value="pago">Pagos</option>
            <option value="credito">{subTab === "proveedor" ? "Pagos a cuenta" : "Créditos"}</option>
          </select>
        )}
        {!loading && puedeEditarSaldoInicial && (
          <button type="button" className={styles.btnSaldoInicial} onClick={() => setEditandoSaldo(true)}>
            {saldoInicialActivo ? "Editar saldo inicial" : "+ Saldo inicial"}
          </button>
        )}
        {!loading && subTab === "proveedor" && selectedEntityId && (
          <button type="button" className={styles.btnSaldoInicial} onClick={() => setMostrarFacturaModal(true)}>
            + Cargar factura
          </button>
        )}
        {!loading && subTab === "caja_chica" && selectedEntityId && (
          <button type="button" className={styles.btnSaldoInicial} onClick={() => setMostrarVincularCajaChica(true)}>
            + Vincular factura
          </button>
        )}
        {!loading && selectedEntityId && subTab !== "edificio" && (
          <select
            className={`form-select ${styles.monedaSelect}`}
            value={monedaSaldo}
            onChange={(e) => setMonedaSaldo(e.target.value)}
          >
            <option value={monedaPrincipal}>{monedaPrincipal}</option>
            <option value="USD">USD</option>
          </select>
        )}
        {!loading && subTab === "edificio" && (
          <select
            className={`form-select ${styles.entitySelect}`}
            value={selectedEntityId}
            onChange={(e) => setSelectedEntityId(e.target.value)}
          >
            <option value="">Seleccionar edificio...</option>
            {edificios.length > 1 && <option value="todos">Todos</option>}
            {uniqueAlianzas.length > 0 && (
              <optgroup label="Alianzas">
                {uniqueAlianzas.map((a) => (
                  <option key={`alianza:${a}`} value={`alianza:${a}`}>
                    {a}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Edificios">
              {[...edificios]
                .sort((a, b) => (a.edificio || "").localeCompare(b.edificio || ""))
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.edificio}
                  </option>
                ))}
            </optgroup>
          </select>
        )}
        {!loading && subTab !== "edificio" && (
          <select
            className={`form-select ${styles.entitySelect}`}
            value={selectedEntityId}
            onChange={(e) => setSelectedEntityId(e.target.value)}
          >
            <option value="">Seleccionar {entityLabel.toLowerCase()}...</option>
            {entityOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
      </Header>
      <div className={styles.content}>
        {loading ? (
          <div className={styles.empty}>Cargando…</div>
        ) : (
          <>
            {!selectedEntityId && (
              <div className={styles.empty}>Seleccione una entidad para ver su estado de cuenta</div>
            )}
            {selectedEntityId && loadingComisiones && subTab === "edificio" && (
              <div className={styles.empty}>Calculando comisiones...</div>
            )}
            {selectedEntityId && !loadingComisiones && ledger.length === 0 && (
              <div className={styles.empty}>Sin movimientos registrados desde 2026-01-01</div>
            )}

            {selectedEntityId && !loadingComisiones && ledger.length > 0 && (
              <>
                <SaldoResumen
                  subTab={subTab}
                  totalFacturadoBruto={totalFacturadoBruto}
                  totalNotasCredito={totalNotasCredito}
                  totalFacturadoNeto={totalFacturadoNeto}
                  totalPagado={totalPagado}
                  creditoNeto={creditoNeto}
                  saldoFinal={saldoFinal}
                  monedaSaldo={monedaSaldo}
                  formatAmount={formatAmount}
                />

                {subTab === "edificio" && (
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                    <div className={styles.vistaToggle}>
                      {VISTAS_EDIFICIO.map((v) => (
                        <button
                          key={v.value}
                          className={`${styles.vistaOption} ${vistaEdificio === v.value ? styles.vistaActiva : ""}`}
                          onClick={() => setVistaEdificio(v.value)}
                        >
                          {v.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className={styles.tableWrapper}>
                  <LedgerTable
                    subTab={subTab}
                    ledger={ledgerFiltrado}
                    monedaSaldo={monedaSaldo}
                    formatAmount={formatAmount}
                    folioPorMovBancario={folioPorMovBancario}
                    ocultarSaldo={filtroTipo !== "todos"}
                    onCambiarTipoPago={handleCambiarTipoPago}
                    onCambiarDescripcion={handleCambiarDescripcion}
                    onEditFacturaExt={setEditandoFacturaExt}
                    onDeleteFacturaExt={handleDeleteFacturaExt}
                    onDesvincularCajaChica={handleDesvincularCajaChica}
                    onDesvincularPago={handleDesvincularPago}
                    onAbrirVincular={handleAbrirVincular}
                    onDesasociarSII={handleDesasociarSII}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      {editandoSaldo && puedeEditarSaldoInicial && (
        <SaldoInicialModal
          tipoLabel={entityLabel}
          saldoActual={saldoInicialActivo}
          monedaPrincipal={monedaPrincipal}
          onClose={() => setEditandoSaldo(false)}
          onGuardar={handleGuardarSaldoInicial}
        />
      )}

      {mostrarFacturaModal && subTab === "proveedor" && selectedEntityId && (
        <FacturaExternaModal
          show={mostrarFacturaModal}
          onClose={() => setMostrarFacturaModal(false)}
          pais={normalizedPais}
          moneda={monedaSaldo}
          proveedorId={Number(selectedEntityId)}
          proveedores={proveedores}
          onFacturaCreada={(fe) => setFacturasExternas((prev) => [...prev, fe])}
        />
      )}

      {editandoFacturaExt && (
        <EditarFacturaExternaModal
          entry={editandoFacturaExt}
          onClose={() => setEditandoFacturaExt(null)}
          onSaved={handleFacturaExtEditada}
        />
      )}

      {showVincularModal && movimientoParaVincular && (
        <VincularMovimientoModal
          show={showVincularModal}
          movimiento={movimientoParaVincular}
          onClose={() => {
            setShowVincularModal(false);
            setMovimientoParaVincular(null);
          }}
          onSuccess={handleVincularSuccess}
        />
      )}

      {mostrarVincularCajaChica && subTab === "caja_chica" && selectedEntityId && (
        <VincularFacturaCajaChicaModal
          show={mostrarVincularCajaChica}
          onClose={() => setMostrarVincularCajaChica(false)}
          siiRecibidas={siiRecibidas}
          cajaChicaGastos={cajaChicaGastos}
          empleadoId={selectedEntityId}
          pais={normalizedPais}
          usuarioId={usuarioId}
          onVinculadas={(rows) => {
            setCajaChicaGastos((prev) => [...prev, ...rows]);
            setMostrarVincularCajaChica(false);
          }}
        />
      )}
    </div>
  );
};

const SaldoResumen = ({
  subTab,
  totalFacturadoBruto,
  totalNotasCredito,
  totalFacturadoNeto,
  totalPagado,
  creditoNeto,
  saldoFinal,
  monedaSaldo,
  formatAmount,
}) => {
  const labelsByTab = {
    // Proveedor: "Pagos a cuenta" en vez de "Crédito" (anticipos/retiros de cuenta corriente).
    proveedor: ["Total facturado", "Total pagado", "Pagos a cuenta", "Saldo"],
    // Cliente: "Crédito" sí aplica (cobros a cuenta del cliente).
    cliente: ["Total facturado", "Total cobrado", "Crédito", "Saldo"],
    // Empleado/edificio/caja chica: el box de crédito es siempre 0 (sus entries no usan bucket
    // "credito") → se deja "Crédito" genérico, no se muestra valor distinto de 0.
    empleado: ["Debe", "Total pagado", "Crédito", "Saldo"],
    edificio: ["Comisión generada", "Total pagado", "Crédito", "Saldo"],
    caja_chica: ["Total entregado", "Total gastado", "Crédito", "Saldo"],
  };
  const [labelDebe, labelHaber, labelCredito, labelSaldo] = labelsByTab[subTab] || [
    "Debe",
    "Haber",
    "Crédito",
    "Saldo",
  ];

  // > 0: deudor (rojo borgoña). < 0: a favor (verde). Cero: neutral. Misma regla para Saldo y Crédito.
  const saldoBg = saldoFinal > 0 ? "#722f37" : saldoFinal < 0 ? "#198754" : "#343a40";
  const creditoBg = creditoNeto > 0 ? "#722f37" : creditoNeto < 0 ? "#198754" : "#343a40";

  // Solo hay layout extendido (6 boxes) cuando existen NCs en el ledger. Sin NCs el layout es
  // idéntico al original: 4 boxes en una fila (facturado neto == bruto).
  const hayNC = totalNotasCredito > 0;

  const cardStyle = {
    borderRadius: 6,
    padding: "12px 24px",
    textAlign: "center",
    color: "#fff",
  };
  const labelStyle = {
    fontSize: "0.75rem",
    fontWeight: 500,
    marginBottom: 5,
    paddingBottom: 5,
    borderBottom: "1px solid rgba(255,255,255,0.3)",
    opacity: 0.9,
    textTransform: "uppercase",
  };
  const valueStyle = { fontSize: "1.05rem", fontWeight: 700, marginTop: 5 };

  const wrapperStyle = { width: hayNC ? "100%" : "75%", margin: "16px auto", padding: "14px 0" };
  const rowStyle = (cols) => ({ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16 });

  const renderCard = (kpi) => (
    <div key={kpi.label} style={{ ...cardStyle, background: kpi.bg }}>
      <div style={labelStyle}>{kpi.label}</div>
      <div style={valueStyle}>{formatAmount(kpi.valor, monedaSaldo)}</div>
    </div>
  );

  if (!hayNC) {
    // Layout clásico de 4 boxes (sin notas de crédito).
    const cards = [
      { label: labelDebe, valor: totalFacturadoNeto, bg: "#343a40" },
      { label: labelHaber, valor: totalPagado, bg: "#343a40" },
      { label: labelCredito, valor: Math.abs(creditoNeto), bg: creditoBg },
      { label: labelSaldo, valor: Math.abs(saldoFinal), bg: saldoBg },
    ];
    return (
      <div style={wrapperStyle}>
        <div style={rowStyle(4)}>{cards.map(renderCard)}</div>
      </div>
    );
  }

  // Layout extendido de 6 boxes en una sola fila: separa facturado bruto / NC / neto.
  const cards = [
    { label: "Facturado bruto", valor: totalFacturadoBruto, bg: "#343a40" },
    { label: "Notas de crédito", valor: totalNotasCredito, bg: "#722f37" },
    { label: "Facturado neto", valor: totalFacturadoNeto, bg: "#343a40" },
    { label: labelHaber, valor: totalPagado, bg: "#343a40" },
    { label: labelCredito, valor: Math.abs(creditoNeto), bg: creditoBg },
    { label: labelSaldo, valor: Math.abs(saldoFinal), bg: saldoBg },
  ];
  return (
    <div style={wrapperStyle}>
      <div style={rowStyle(6)}>{cards.map(renderCard)}</div>
    </div>
  );
};

const LedgerTable = ({
  subTab,
  ledger,
  monedaSaldo,
  formatAmount,
  folioPorMovBancario,
  ocultarSaldo = false,
  onCambiarTipoPago,
  onCambiarDescripcion,
  onEditFacturaExt,
  onDeleteFacturaExt,
  onDesvincularCajaChica,
  onDesvincularPago,
  onAbrirVincular,
  onDesasociarSII,
}) => {
  const isEdificio = subTab === "edificio";
  // cajaId del pago cuyo tipo se está reclasificando (null = ninguno) + posición del menú
  // flotante (fixed, vía portal a document.body → no lo recorta el overflow de la tabla).
  const [editandoTipoCajaId, setEditandoTipoCajaId] = useState(null);
  const [tipoMenuAnchor, setTipoMenuAnchor] = useState({ top: 0, left: 0 });
  const tipoMenuRef = useRef(null);

  // Cerrar el menú de Tipo pago al hacer click fuera de él.
  useEffect(() => {
    if (editandoTipoCajaId == null) return undefined;
    const onDocMouseDown = (e) => {
      if (tipoMenuRef.current && tipoMenuRef.current.contains(e.target)) return;
      setEditandoTipoCajaId(null);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [editandoTipoCajaId]);
  // cajaId del movimiento cuyo concepto se está editando inline + borrador del texto.
  const [editandoDescCajaId, setEditandoDescCajaId] = useState(null);
  const [descDraft, setDescDraft] = useState("");
  const showFolio = subTab !== "edificio" && subTab !== "empleado" && subTab !== "caja_chica";
  const showAcciones = subTab === "proveedor";
  const showAccionesCajaChica = subTab === "caja_chica";

  // Mezcla de monedas: si hay entries con moneda distinta a la del saldo, se separan
  // las columnas Debe/Haber por moneda. Cada entry aparece una sola vez en la columna
  // de su moneda nativa (sin conversión). El Saldo siempre va en monedaSaldo.
  const hayMezclaMonedas = !isEdificio && ledger.some((e) => e.moneda && e.moneda !== monedaSaldo);
  const monedaSecundaria = hayMezclaMonedas ? ledger.find((e) => e.moneda && e.moneda !== monedaSaldo)?.moneda : null;

  const tableClass = [
    styles.estadoCuentaTable,
    isEdificio ? styles.estadoCuentaEdificio : "",
    hayMezclaMonedas ? styles.estadoCuentaMezcla : "",
    showFolio ? styles.estadoCuentaConFolio : "",
    showAcciones ? styles.estadoCuentaConAcciones : "",
    // Con la columna Saldo oculta (filtro activo) el conteo de columnas cambia y las variantes
    // nth-child de ancho fijo desalinean → se pasa a table-layout:auto vía esta clase.
    ocultarSaldo ? styles.estadoCuentaAuto : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Columna TIPO (solo proveedor): clasifica los PAGOS (origen null + creditoDisplay>0) por su
  // categoriaPago. Facturas, NCs, cobros a cuenta y saldo inicial → celda vacía (no son pagos).
  const renderTipoPago = (entry) => {
    // Reclasificables: movimientos de caja (origen null) — pagos (creditoDisplay>0) o
    // cobros a cuenta (debitoDisplay>0). Facturas/NC/saldo inicial → celda vacía.
    const esPago = entry.creditoDisplay > 0;
    const esCobro = entry.debitoDisplay > 0;
    if (!(entry.origen === null && (esPago || esCobro))) return <span style={{ color: "#999" }}>—</span>;
    const base = { fontSize: "0.78rem" };
    // Editable inline solo si tiene un movimiento_caja real (cajaId).
    const editable = !!entry.cajaId && typeof onCambiarTipoPago === "function";

    // Opciones del select según sea pago (pago_*) o cobro (cobro_*).
    const opciones = esCobro
      ? [
          { value: "cobro_factura", label: "Cobro", color: "#198754" },
          { value: "cobro_a_cuenta", label: "A cuenta", color: "#e67e22" },
          { value: "cobro_credito", label: "Crédito", color: "#722f37" },
        ]
      : [
          { value: "pago_factura", label: "Factura", color: "#198754" },
          { value: "pago_a_cuenta", label: "A cuenta", color: "#e67e22" },
          { value: "pago_credito", label: "Crédito", color: "#722f37" },
        ];

    // Label (clickeable si editable → abre el menú flotante de reclasificación).
    let label;
    let color;
    switch (entry.categoriaPago) {
      case "pago_factura": {
        const folio = entry.tieneMBF ? folioPorMovBancario?.get(entry.movimientoBancarioId) : null;
        label = folio ? `Factura #${folio}` : "Factura";
        color = "#198754";
        break;
      }
      case "pago_a_cuenta":
        label = "A cuenta";
        color = "#e67e22";
        break;
      case "pago_credito":
        label = "Crédito";
        color = "#722f37";
        break;
      case "cobro_factura":
        label = "Cobro";
        color = "#198754";
        break;
      case "cobro_a_cuenta":
        label = "A cuenta";
        color = "#e67e22";
        break;
      case "cobro_credito":
        label = "Crédito";
        color = "#722f37";
        break;
      default:
        label = "Sin clasificar";
        color = "#999";
    }
    if (!editable) return <span style={{ ...base, color }}>{label}</span>;
    const abierto = editandoTipoCajaId === entry.cajaId;
    return (
      <>
        <span
          style={{ ...base, color, cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: 2 }}
          title={esCobro ? "Cambiar tipo de cobro" : "Cambiar tipo de pago"}
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setTipoMenuAnchor({ top: r.bottom + 2, left: r.left });
            setEditandoTipoCajaId(entry.cajaId);
          }}
        >
          {label}
        </span>
        {abierto &&
          createPortal(
            <div
              ref={tipoMenuRef}
              style={{
                position: "fixed",
                top: tipoMenuAnchor.top,
                left: tipoMenuAnchor.left,
                background: "#fff",
                border: "1px solid #dee2e6",
                borderRadius: 6,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                padding: "4px 0",
                minWidth: 120,
                zIndex: 1050,
              }}
            >
              {opciones.map((o) => {
                const seleccionada = o.value === entry.categoriaPago;
                return (
                  <div
                    key={o.value}
                    style={{
                      padding: "6px 12px",
                      fontSize: "0.78rem",
                      cursor: "pointer",
                      color: o.color,
                      fontWeight: seleccionada ? 500 : 400,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f8f9fa")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    onClick={() => {
                      onCambiarTipoPago(entry, o.value);
                      setEditandoTipoCajaId(null);
                    }}
                  >
                    {o.label}
                  </div>
                );
              })}
            </div>,
            document.body
          )}
      </>
    );
  };

  // Celda CONCEPTO. Para pagos/cobros (origen null + cajaId) es editable inline: click → input
  // de texto (maxLength 80) que persiste descripcion al blur/Enter. El resto es texto estático.
  // El texto se trunca a 80 (renderConcepto); el completo va en el title del <td>.
  const renderConceptoCell = (entry) => {
    const editable = entry.origen === null && !!entry.cajaId && typeof onCambiarDescripcion === "function";

    if (editable && editandoDescCajaId === entry.cajaId) {
      return (
        <input
          type="text"
          autoFocus
          maxLength={80}
          value={descDraft}
          onChange={(e) => setDescDraft(e.target.value)}
          onBlur={() => {
            onCambiarDescripcion(entry, descDraft);
            setEditandoDescCajaId(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          style={{
            width: "100%",
            fontSize: "inherit",
            border: "none",
            borderBottom: "1px solid #8884d8",
            outline: "none",
            background: "transparent",
            padding: "0 2px",
          }}
        />
      );
    }

    if (!editable) return renderConcepto(entry);
    return (
      <span
        style={{ cursor: "pointer" }}
        onClick={() => {
          setDescDraft(entry.descripcion || "");
          setEditandoDescCajaId(entry.cajaId);
        }}
      >
        {renderConcepto(entry)}
      </span>
    );
  };

  return (
    <table className={tableClass}>
      <thead>
        <tr>
          {isEdificio ? (
            <th>Período</th>
          ) : (
            <>
              <th>Fecha</th>
              {showFolio && <th>Folio</th>}
              <th>Concepto</th>
            </>
          )}
          {hayMezclaMonedas ? (
            <>
              <th>Debe {monedaSecundaria}</th>
              <th>Haber {monedaSecundaria}</th>
              <th>Debe {monedaSaldo}</th>
              <th>Haber {monedaSaldo}</th>
            </>
          ) : (
            <>
              <th>Debe</th>
              <th>Haber</th>
            </>
          )}
          {showAcciones && <th>Tipo pago</th>}
          {!ocultarSaldo && <th>Saldo {monedaSaldo}</th>}
          {showAcciones && <th>Acciones</th>}
          {showAccionesCajaChica && <th style={{ width: "12%" }}>Acciones</th>}
        </tr>
      </thead>
      <tbody>
        {ledger.map((entry, idx) => {
          const monedaEntry = entry.moneda || monedaSaldo;
          const enSecundaria = hayMezclaMonedas && monedaEntry === monedaSecundaria;
          const enPrincipal = hayMezclaMonedas && monedaEntry === monedaSaldo;

          return (
            <tr key={idx}>
              {isEdificio ? (
                <td>{entry.concepto}</td>
              ) : (
                <>
                  <td>{formatFechaDisplay(entry.fecha)}</td>
                  {showFolio && (
                    <td>
                      {["SII", "EXT"].includes(entry.origen) && entry.referencia && entry.referencia !== "-" ? (
                        String(entry.referencia)
                      ) : (
                        <span style={{ color: "#999" }}>—</span>
                      )}
                    </td>
                  )}
                  <td title={conceptoFull(entry)}>{renderConceptoCell(entry)}</td>
                </>
              )}
              {hayMezclaMonedas ? (
                <>
                  <td style={{ color: enSecundaria && entry.debito > 0 ? "#c62828" : undefined }}>
                    {enSecundaria && entry.debito > 0 ? formatAmount(entry.debito, monedaSecundaria) : ""}
                  </td>
                  <td style={{ color: enSecundaria && entry.credito > 0 ? "#2e7d32" : undefined }}>
                    {enSecundaria && entry.credito > 0 ? formatAmount(entry.credito, monedaSecundaria) : ""}
                  </td>
                  <td style={{ color: enPrincipal && entry.debito !== 0 ? "#c62828" : undefined }}>
                    {enPrincipal && entry.debito !== 0 ? formatAmount(entry.debito, monedaSaldo) : ""}
                  </td>
                  <td style={{ color: enPrincipal && entry.credito > 0 ? "#2e7d32" : undefined }}>
                    {enPrincipal && entry.credito > 0 ? formatAmount(entry.credito, monedaSaldo) : ""}
                  </td>
                </>
              ) : (
                <>
                  <td style={{ color: entry.debito !== 0 ? "#c62828" : undefined }}>
                    {entry.debito !== 0 ? formatAmount(entry.debito, monedaEntry) : ""}
                  </td>
                  <td style={{ color: entry.credito > 0 ? "#2e7d32" : undefined }}>
                    {entry.credito > 0 ? formatAmount(entry.credito, monedaEntry) : ""}
                  </td>
                </>
              )}
              {showAcciones && <td style={{ textAlign: "center" }}>{renderTipoPago(entry)}</td>}
              {!ocultarSaldo && (
                <td
                  style={{
                    fontWeight: 600,
                    color: entry.saldo > 0 ? "#c62828" : entry.saldo < 0 ? "#2e7d32" : "#333",
                  }}
                >
                  {formatAmount(Math.abs(entry.saldo), monedaSaldo)}
                </td>
              )}
              {showAcciones && (
                <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                  {entry.origen === "EXT" ? (
                    <>
                      <FaEdit
                        style={{ cursor: "pointer", fontSize: 14, color: "#495057", marginRight: 10 }}
                        title="Editar"
                        onClick={() => onEditFacturaExt?.(entry)}
                      />
                      <FaTrash
                        style={{ cursor: "pointer", fontSize: 14, color: "#495057", marginRight: 10 }}
                        title="Eliminar"
                        onClick={() => onDeleteFacturaExt?.(entry)}
                      />
                      {entry.archivo_url && (
                        <FaFilePdf
                          style={{ cursor: "pointer", fontSize: 14, color: "#495057" }}
                          title="Ver documento"
                          onClick={() => window.open(entry.archivo_url, "_blank", "noopener,noreferrer")}
                        />
                      )}
                    </>
                  ) : entry.origen === "SII" ? (
                    <FaTrash
                      style={{ cursor: "pointer", fontSize: 14, color: "#495057" }}
                      title="Desasociar del proveedor"
                      onClick={() => onDesasociarSII?.(entry)}
                    />
                  ) : entry.origen === null && entry.cajaId && entry.creditoDisplay > 0 ? (
                    <>
                      {!entry.tieneMBF && (
                        <FaEdit
                          style={{ cursor: "pointer", fontSize: 14, color: "#495057", marginRight: 10 }}
                          title="Vincular a factura"
                          onClick={() => onAbrirVincular?.(entry)}
                        />
                      )}
                      <FaTrash
                        style={{ cursor: "pointer", fontSize: 14, color: "#495057" }}
                        title="Desvincular pago"
                        onClick={() => onDesvincularPago?.(entry)}
                      />
                    </>
                  ) : null}
                </td>
              )}
              {showAccionesCajaChica && (
                <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                  {entry._cajaChicaGastoId ? (
                    <FaTrash
                      style={{ cursor: "pointer", fontSize: 14, color: "#495057" }}
                      title="Desvincular de caja chica"
                      onClick={() => onDesvincularCajaChica?.(entry)}
                    />
                  ) : null}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const SaldoInicialModal = ({ tipoLabel, saldoActual, monedaPrincipal, onClose, onGuardar }) => {
  const [monto, setMonto] = useState(saldoActual ? String(saldoActual.monto) : "");
  const [moneda, setMoneda] = useState(saldoActual?.moneda || monedaPrincipal);
  const [notas, setNotas] = useState(saldoActual?.notas || "");
  const [guardando, setGuardando] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const montoNum = Number(monto);
    if (Number.isNaN(montoNum)) {
      toast.error("Monto inválido");
      return;
    }
    setGuardando(true);
    try {
      await onGuardar({ monto: montoNum, moneda, notas: notas.trim() });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          background: "#fff",
          borderRadius: 8,
          width: "min(440px, 95vw)",
          padding: 20,
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h5 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#1a3a5c" }}>
            Saldo inicial al 01/01/2026 — {tipoLabel}
          </h5>
          <button
            type="button"
            onClick={onClose}
            style={{ border: "none", background: "transparent", fontSize: "1.4rem", cursor: "pointer", color: "#666" }}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div style={{ fontSize: "0.78rem", color: "#666", marginBottom: 12 }}>
          Positivo = la entidad nos debe. Negativo = le debemos a la entidad.
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: "0.78rem", color: "#555", marginBottom: 4 }}>Monto</label>
          <input
            type="number"
            step="any"
            className="form-control form-control-sm"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            autoFocus
            required
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: "0.78rem", color: "#555", marginBottom: 4 }}>Moneda</label>
          <select className="form-select form-select-sm" value={moneda} onChange={(e) => setMoneda(e.target.value)}>
            <option value={monedaPrincipal}>{monedaPrincipal}</option>
            <option value="USD">USD</option>
            {monedaPrincipal !== "CLP" && <option value="CLP">CLP</option>}
            {monedaPrincipal !== "PEN" && <option value="PEN">PEN</option>}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: "0.78rem", color: "#555", marginBottom: 4 }}>
            Notas (opcional)
          </label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Ej: cierre 2025, pendiente de cobro Empresa X"
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-sm btn-primary" disabled={guardando}>
            {guardando ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
};

const EditarFacturaExternaModal = ({ entry, onClose, onSaved }) => {
  const refInicial = entry.referencia && entry.referencia !== "-" ? entry.referencia : "";
  const [fecha, setFecha] = useState(entry.fecha);
  const [periodoMes, setPeriodoMes] = useState(() =>
    entry.periodo_mes ? defaultPeriodoMes(entry.periodo_mes) : defaultPeriodoMes(entry.fecha)
  );
  const [numeroDoc, setNumeroDoc] = useState(refInicial);
  const [concepto, setConcepto] = useState(entry.concepto || "");
  const [monto, setMonto] = useState(String(entry.debito ?? ""));
  const [saving, setSaving] = useState(false);

  const handleGuardar = async () => {
    const montoNum = Number(monto || 0);
    if (!fecha) return toast.error("Ingresá la fecha del documento");
    if (!concepto.trim()) return toast.error("Ingresá el concepto");
    if (montoNum <= 0) return toast.error("Ingresá el monto");
    setSaving(true);
    try {
      const { data, error } = await client
        .from("facturas_externas")
        .update({
          fecha_emision: fecha,
          periodo_mes: periodoMesToString(periodoMes),
          numero_documento: numeroDoc.trim() || null,
          concepto: concepto.trim(),
          monto_neto: montoNum,
          monto_total: montoNum,
        })
        .eq("id", entry.facturaId)
        .select()
        .single();
      if (error) throw error;
      toast.success("Factura actualizada");
      onSaved(data);
    } catch (err) {
      console.error("Error actualizando factura externa:", err);
      toast.error("Error al guardar: " + (err.message || ""));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Editar factura externa</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Fecha documento</div>
          <input
            type="date"
            className="form-control form-control-sm"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            style={{ fontSize: "0.82rem" }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Período</div>
          <div style={{ display: "flex", gap: 8 }}>
            <select
              className="form-select form-select-sm"
              value={periodoMes.getMonth()}
              onChange={(e) => setPeriodoMes(new Date(periodoMes.getFullYear(), Number(e.target.value), 1))}
              style={{ fontSize: "0.82rem", flex: 1 }}
            >
              {MESES_LABELS.map((label, i) => (
                <option key={i} value={i}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={periodoMes.getFullYear()}
              onChange={(e) => setPeriodoMes(new Date(Number(e.target.value), periodoMes.getMonth(), 1))}
              style={{ fontSize: "0.82rem", width: 100 }}
            >
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>N° documento (opcional)</div>
          <input
            type="text"
            className="form-control form-control-sm"
            value={numeroDoc}
            onChange={(e) => setNumeroDoc(e.target.value)}
            style={{ fontSize: "0.82rem" }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Concepto</div>
          <input
            type="text"
            className="form-control form-control-sm"
            value={concepto}
            onChange={(e) => setConcepto(e.target.value)}
            style={{ fontSize: "0.82rem" }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
            Monto {entry.moneda ? `(${entry.moneda})` : ""}
          </div>
          <input
            type="text"
            inputMode="decimal"
            className="form-control form-control-sm"
            placeholder="0.00"
            value={monto}
            onChange={(e) => {
              const normalized = e.target.value.replace(",", ".");
              if (normalized === "" || /^\d*\.?\d*$/.test(normalized)) {
                setMonto(normalized);
              }
            }}
            style={{ fontSize: "0.82rem" }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Vincula facturas SII recibidas al fondo de caja chica de un empleado. Inserta una fila
// por factura en caja_chica_gastos (factura_tipo='sii_recibida'). Las facturas vinculadas
// dejan de aparecer en "Por pagar" de FlujoCajaV2 (se filtran por factura_id).
const VincularFacturaCajaChicaModal = ({
  show,
  onClose,
  siiRecibidas,
  cajaChicaGastos,
  empleadoId,
  pais,
  usuarioId,
  onVinculadas,
}) => {
  const { formatAmount } = useCurrency();
  const monedaPais = pais === "Chile" ? "CLP" : "PEN";
  const [busqueda, setBusqueda] = useState("");
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  // Clasificación contable (dimensión independiente de la vinculación a caja chica).
  const [tipoContable, setTipoContable] = useState("gasto");
  const [cuentaER, setCuentaER] = useState("");

  // IDs ya vinculados a caja chica (cualquier empleado) — se excluyen de la lista.
  const yaVinculadas = useMemo(
    () => new Set((cajaChicaGastos || []).filter((g) => g.factura_tipo === "sii_recibida").map((g) => g.factura_id)),
    [cajaChicaGastos]
  );

  const disponibles = useMemo(
    () =>
      (siiRecibidas || [])
        .filter(
          (f) =>
            f.tipo_dte !== 61 &&
            f.estado_vinculacion !== "vinculada" &&
            f.estado_vinculacion !== "ignorada" &&
            !yaVinculadas.has(f.id)
        )
        .sort((a, b) => (a.fecha_emision || "").localeCompare(b.fecha_emision || "")),
    [siiRecibidas, yaVinculadas]
  );

  const filtradas = useMemo(() => {
    if (!busqueda.trim()) return disponibles;
    const t = busqueda.trim().toLowerCase();
    return disponibles.filter(
      (f) => (f.razon_social_emisor || "").toLowerCase().includes(t) || String(f.folio || "").includes(t)
    );
  }, [disponibles, busqueda]);

  const toggle = (f) => {
    setSeleccionadas((prev) => (prev.some((x) => x.id === f.id) ? prev.filter((x) => x.id !== f.id) : [...prev, f]));
  };

  const handleConfirmar = async () => {
    if (seleccionadas.length === 0) return toast.error("Seleccioná al menos una factura");
    if (tipoContable === "gasto" && !cuentaER) return toast.error("Seleccioná la cuenta ER del gasto");
    setGuardando(true);
    try {
      const rows = seleccionadas.map((f) => ({
        empleado_id: empleadoId,
        factura_tipo: "sii_recibida",
        factura_id: f.id,
        monto: f.monto_total,
        fecha: f.fecha_emision,
        concepto: `Factura ${f.folio} - ${f.razon_social_emisor || ""}`.trim(),
        pais,
        created_by: usuarioId,
      }));
      const { data, error } = await client.from("caja_chica_gastos").insert(rows).select();
      if (error) throw error;

      // Clasificación contable + marcar vinculada (destino contable + fuente financiera resueltos).
      const clasificacionUpdates = seleccionadas.map((f) =>
        client
          .from("facturas_sii_recibidas")
          .update({
            tipo_contable: tipoContable,
            cuenta_er: tipoContable === "gasto" ? cuentaER : null,
            estado_vinculacion: "vinculada",
            vinculado_at: new Date().toISOString(),
          })
          .eq("id", f.id)
      );
      await Promise.all(clasificacionUpdates);

      toast.success(
        seleccionadas.length === 1 ? "Factura vinculada a caja chica" : `${seleccionadas.length} facturas vinculadas`
      );
      onVinculadas(data || []);
    } catch (err) {
      console.error("Error vinculando facturas a caja chica:", err);
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Vincular factura a caja chica</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Buscar por proveedor o folio..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ marginBottom: 10 }}
        />
        <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
          {filtradas.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin facturas disponibles</div>
          ) : (
            filtradas.map((f) => {
              const sel = seleccionadas.some((x) => x.id === f.id);
              return (
                <div
                  key={f.id}
                  onClick={() => toggle(f)}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "0.83rem",
                    borderBottom: "1px solid #f0f0f0",
                    background: sel ? "#e8f4fd" : "transparent",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <span>
                      {f.fecha_emision ? `${f.fecha_emision.substring(8, 10)}/${f.fecha_emision.substring(5, 7)}` : "-"}
                    </span>
                    <span style={{ margin: "0 10px", color: "#999" }}>|</span>
                    <span>{f.razon_social_emisor || "—"}</span>
                    <span style={{ marginLeft: 8, color: "#999" }}>Folio {f.folio}</span>
                  </div>
                  <div style={{ color: "#c0392b", fontWeight: 600 }}>{formatAmount(f.monto_total, monedaPais)}</div>
                </div>
              );
            })
          )}
        </div>
        {seleccionadas.length > 0 && (
          <div style={{ marginTop: 10, fontSize: "0.82rem", fontWeight: 600 }}>
            {seleccionadas.length} factura{seleccionadas.length === 1 ? "" : "s"} seleccionada
            {seleccionadas.length === 1 ? "" : "s"}
          </div>
        )}
        {seleccionadas.length > 0 && (
          <div style={{ padding: "12px 0", borderTop: "1px solid #eee", marginTop: 12 }}>
            <div style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: 8 }}>Clasificación contable</div>
            <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: "0.85rem" }}>
                <input
                  type="radio"
                  name="tipoContable"
                  value="gasto"
                  checked={tipoContable === "gasto"}
                  onChange={() => setTipoContable("gasto")}
                />
                Gasto
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: "0.85rem" }}>
                <input
                  type="radio"
                  name="tipoContable"
                  value="activo"
                  checked={tipoContable === "activo"}
                  onChange={() => setTipoContable("activo")}
                />
                Activo
              </label>
            </div>
            {tipoContable === "gasto" && (
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: 400 }}
                value={cuentaER}
                onChange={(e) => setCuentaER(e.target.value)}
              >
                <option value="">Seleccionar cuenta ER...</option>
                {Object.entries(CUENTAS_POR_GRUPO).map(([grupo, cuentas]) => (
                  <optgroup key={grupo} label={grupo}>
                    {cuentas.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleConfirmar}
          disabled={guardando || seleccionadas.length === 0 || (tipoContable === "gasto" && !cuentaER)}
        >
          {guardando ? "Vinculando..." : "Vincular"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EstadoCuenta;
